'use client'

import AppointmentCoupon from '@/components/appointment-coupon'
import BillingInfoSection, { BillingInfoData } from '@/components/billing/BillingInfoSection'
import BillingChildrenSections from '@/components/billing/billing-children-sections'
import ConfirmModal from '@/components/confirm-modal'
import ActionsMenu, { ActionMenuItem } from '@/components/actions-menu'
import ProntuarioMenu from '@/components/prontuario/prontuario-menu'
import { ExamRequestButton } from '@/components/medical/exam-request-modal'
import CancelAppointmentModal from '@/components/medical/cancel-appointment-modal'
import TransferSellerModal from '@/components/medical/transfer-seller-modal'
import ScheduleAppointmentModal from '@/components/scheduling/schedule-appointment-modal'
import ChangeServiceAddressModal from '@/components/service-address/change-service-address-modal'
import ServiceAddressCard from '@/components/service-address/service-address-card'
import { ServiceAddress } from '@/components/service-address/service-address-section'
import ChangeStatusModal from '@/components/bookings/ChangeStatusModal'
import { UserType } from '@/lib/api/common/interfaces/user.interface'
import CancellationNotice from '@/components/medical/cancellation-notice'
import { AiSummaryCard } from '@/components/consultation/ai-summary-card'
import { ConsultationTimeline } from '@/components/consultation/consultation-timeline'
import { TranscriptionViewer } from '@/components/consultation/transcription-viewer'
import { SignDocumentModal } from '@/components/signature/SignDocumentModal'
import {
    analyzeConsultation,
    cancelConsultationBilling,
    Consultation,
    getConsultationStatusColor,
    getConsultationStatusLabel,
    cancelConsultation,
    transferConsultationSeller,
    generateConsultationBilling,
    deleteConsultation,
    getConsultation,
    markConsultationBillingAsPaid,
    printConsultation,
    regenerateConsultationSummary,
    renewConsultationGuide,
    transcribeConsultation,
    changeConsultationStatus,
    CONSULTATION_STATUS_LABELS,
    updateConsultationServiceAddress,
    type ConsultationStatus,

} from '@/lib/api/consultations/consultation-api'
import { PdfDelivery } from '@/lib/download-blob'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { GuideValidityLine } from '@/components/guide-validity-line'
import { computeRenewedGuideValidUntil, useGuideValidityDays } from '@/lib/guide-validity'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useContext, useEffect, useState } from 'react'
import { SocketContext } from '@/lib/providers/socket-provider'
import {
    resolveAppointmentBillingState,
    type AppointmentAnchorLike,
} from '@/lib/billing/appointment-billing-state'
import PresenceTimeline from '@/components/presence/presence-timeline'
import {
    MdAccessTime,
    MdArrowBack,
    MdAutoAwesome,
    MdBusiness,
    MdLocalHospital,
    MdCalendarToday,
    MdCancel,
    MdDelete,
    MdEdit,
    MdSwapHoriz,
    MdReceiptLong,
    MdEvent,
    MdEventRepeat,
    MdHeadset,
    MdManageAccounts,
    MdMedicalServices,
    MdMic,
    MdOpenInNew,
    MdLocationOn,
    MdPerson,
    MdPictureAsPdf,
    MdPlayArrow,
    MdDownload,
    MdPrint,
    MdStorage,
    MdTrendingUp,
    MdMeetingRoom,
    MdFlag,

} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

interface ConsultationDetailPageProps {
    /** Renderiza embutido (modal) para este id, ignorando a rota. */
    idOverride?: number
    /** Substitui o "voltar para a lista" por fechar o modal. */
    onBack?: () => void
}

export default function ConsultationDetailPage({ idOverride, onBack }: ConsultationDetailPageProps = {}) {
    const navigate = useNavigate()
    const { user } = useAuth()
    const guideValidityDays = useGuideValidityDays(user?.companyId)
    const { canUpdate, canDelete } = useResourcePermissions('bookings', 'consultations')
    // Fora da saúde: Avaliação.
    const {
        consultation: consultationLabel,
        Consultation: ConsultationLabel,
    } = useServiceLabel()
    const { id: paramId } = useParams<{ id: string }>()
    const id = idOverride ? String(idOverride) : paramId
    const { socket, isConnected } = useContext(SocketContext)

    // Pagamento confirmado pelo gateway: o webhook invalida o cache DESTE
    // atendimento e emite este evento. Sem escutar aqui, a cobrança seguia
    // aparecendo como pendente na tela até o operador recarregar.
    useEffect(() => {
        if (!socket || !isConnected || !id) return
        const eventName = `appointment-billing-consultation-${id}`
        const handle = () => loadConsultation()
        socket.on(eventName, handle)
        return () => {
            socket.off(eventName, handle)
        }
    }, [socket, isConnected, id])
    /** No modal, sair da lista significa fechar; na rota, navegar. */
    const goBack = () => (onBack ? onBack() : navigate('/bookings/consultations'))
    const isIntermediation = (user?.company as any)?.secondarySegment === 'INTERMEDIATION'
    const [consultation, setConsultation] = useState<Consultation | null>(null)
    const [loading, setLoading] = useState(true)
    const [deleting, setDeleting] = useState(false)
    const [showScheduleModal, setShowScheduleModal] = useState(false)
    const [showStatusModal, setShowStatusModal] = useState(false)
    const [savingStatus, setSavingStatus] = useState(false)
    const [transcribing, setTranscribing] = useState(false)
    const [analyzing, setAnalyzing] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [cancelling, setCancelling] = useState(false)
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [transferring, setTransferring] = useState(false)
    const [showTransferModal, setShowTransferModal] = useState(false)
    const [showAddressModal, setShowAddressModal] = useState(false)
    const [savingAddress, setSavingAddress] = useState(false)
    const [printing, setPrinting] = useState<boolean>(false)
    const [showSignModal, setShowSignModal] = useState<boolean>(false)

    useEffect(() => {
        if (id) {
            loadConsultation()
        }
    }, [id])

    const loadConsultation = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getConsultation(parseInt(id, 10))
            if (response.success && response.result) {
                setConsultation(response.result)
            } else {
                toast.error(
                    response.error || `Erro ao carregar ${consultationLabel}`
                )
                goBack()
            }
        } catch (error) {
            toast.error(`Erro ao carregar ${consultationLabel}`)
            goBack()
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = () => {
        setShowDeleteModal(true)
    }

    /**
     * Cancelar não é excluir: a consulta permanece na listagem como "Cancelada".
     * Se a cobrança estava paga, o backend responde `refundPending` — o dinheiro só
     * volta depois que um operador financeiro aprovar o estorno.
     */
    const confirmCancel = async (reason: string) => {
        if (!id || !consultation) return

        setCancelling(true)
        try {
            const response = await cancelConsultation(parseInt(id, 10), reason)
            if (response.success) {
                if (response.result?.refundPending) {
                    toast.success(
                        `${ConsultationLabel} cancelada. A solicitação de estorno foi enviada para aprovação de um operador financeiro.`,
                    )
                } else {
                    toast.success(`${ConsultationLabel} cancelada com sucesso!`)
                }
                setShowCancelModal(false)
                await loadConsultation()
            } else {
                toast.error(
                    response.error || `Erro ao cancelar ${consultationLabel}`
                )
            }
        } catch (error) {
            toast.error(`Erro ao cancelar ${consultationLabel}`)
        } finally {
            setCancelling(false)
        }
    }

    // Transferir a venda (createdBy) para outro usuário. Autorização real no backend
    // (só COMPANY ou o vendedor atual); a ação nem aparece para os demais.
    const confirmTransferSeller = async (sellerId: number) => {
        if (!id || !consultation) return
        setTransferring(true)
        try {
            const response = await transferConsultationSeller(parseInt(id, 10), sellerId)
            if (response.success) {
                toast.success('Vendedor transferido com sucesso!')
                setShowTransferModal(false)
                await loadConsultation()
            } else {
                toast.error(response.error || 'Erro ao transferir vendedor')
            }
        } catch {
            toast.error('Erro ao transferir vendedor')
        } finally {
            setTransferring(false)
        }
    }

    // Gerar cobrança para uma consulta que ficou sem cobrança. Após criar, vai direto à cobrança.
    const [generatingBilling, setGeneratingBilling] = useState(false)
    const handleGenerateBilling = async () => {
        if (!id || !consultation) return
        setGeneratingBilling(true)
        try {
            const response = await generateConsultationBilling(parseInt(id, 10))
            if (response.success && response.result?.billingId) {
                toast.success('Cobrança gerada com sucesso!')
                // Vai para a EDIÇÃO (escolher forma de pagamento, vencimento etc.), com
                // `from=schedule` para exibir cliente/parceiro herdados como no agendamento.
                navigate(
                    `/financial/billings/${response.result.billingId}/edit?from=schedule`,
                )
            } else {
                toast.error(response.error || 'Erro ao gerar cobrança')
            }
        } catch {
            toast.error('Erro ao gerar cobrança')
        } finally {
            setGeneratingBilling(false)
        }
    }

    const [showRenewGuideModal, setShowRenewGuideModal] = useState(false)
    const [renewingGuide, setRenewingGuide] = useState(false)
    const handleRenewGuide = async () => {
        if (!id) return
        setRenewingGuide(true)
        try {
            const response = await renewConsultationGuide(parseInt(id, 10))
            if (response.success) {
                toast.success('Guia renovada com sucesso')
                setShowRenewGuideModal(false)
                await loadConsultation()
            } else {
                toast.error(response.error || 'Erro ao renovar a guia')
            }
        } catch {
            toast.error('Erro ao renovar a guia')
        } finally {
            setRenewingGuide(false)
        }
    }

    const confirmDelete = async () => {
        if (!id || !consultation) return

        setDeleting(true)
        try {
            const response = await deleteConsultation(parseInt(id, 10))
            if (response.success) {
                toast.success(`${ConsultationLabel} excluída com sucesso!`)
                goBack()
            } else {
                toast.error(
                    response.error || `Erro ao excluir ${consultationLabel}`
                )
            }
        } catch (error) {
            toast.error(`Erro ao excluir ${consultationLabel}`)
        } finally {
            setDeleting(false)
            setShowDeleteModal(false)
        }
    }

    const handleTranscribe = async () => {
        if (!id) return

        setTranscribing(true)
        try {
            const response = await transcribeConsultation(parseInt(id, 10))
            if (response.success) {
                toast.success('Transcrição iniciada com sucesso!')
                loadConsultation()
            } else {
                toast.error(response.error || 'Erro ao iniciar transcrição')
            }
        } catch (error) {
            toast.error('Erro ao iniciar transcrição')
        } finally {
            setTranscribing(false)
        }
    }

    const handleAnalyze = async () => {
        if (!id) return

        setAnalyzing(true)
        try {
            const response = await analyzeConsultation(parseInt(id, 10))
            if (response.success) {
                toast.success('Análise IA concluída!')
                loadConsultation()
            } else {
                toast.error(response.error || 'Erro ao gerar análise IA')
            }
        } catch (error) {
            toast.error('Erro ao gerar análise IA')
        } finally {
            setAnalyzing(false)
        }
    }

    const handleRegenerateSummary = async () => {
        if (!id) return

        setAnalyzing(true)
        try {
            const response = await regenerateConsultationSummary(parseInt(id, 10))
            if (response.success) {
                toast.success('Resumo regenerado com sucesso!')
                loadConsultation()
            } else {
                toast.error(response.error || 'Erro ao regenerar resumo')
            }
        } catch (error) {
            toast.error('Erro ao regenerar resumo')
        } finally {
            setAnalyzing(false)
        }
    }

    /**
     * `delivery` decide entre abrir a impressão e salvar o arquivo. "Imprimir"
     * abre o diálogo direto — o operador quer o papel, não um PDF na pasta de
     * downloads; "Baixar PDF" fica como ação separada no menu.
     */
    const handlePrint = async (delivery: PdfDelivery = 'print') => {
        if (!consultation) return
        setPrinting(true)
        try {
            await printConsultation(consultation.id, delivery)
            if (delivery === 'download') toast.success('PDF gerado com sucesso!')
        } catch (error) {
            toast.error('Erro ao gerar PDF')
        } finally {
            setPrinting(false)
        }
    }

    // Estado financeiro da consulta (âncora + complementos). Só cobrança VIVA — ou
    // dinheiro que entrou e não voltou — bloqueia a geração de uma nova.
    const billingState = resolveAppointmentBillingState(
        (consultation as any)?.billing as AppointmentAnchorLike | null | undefined,
        0
    )

    /**
     * Troca o local de atendimento já gravado. Endpoint próprio: a edição completa
     * mexeria em preço e cobrança, que nada têm a ver com corrigir o endereço.
     */
    const handleChangeServiceAddress = async (address: ServiceAddress) => {
        if (!consultation) return
        setSavingAddress(true)
        try {
            const response = await updateConsultationServiceAddress(
                consultation.id,
                address,
            )
            if (response.success) {
                toast.success('Local de atendimento atualizado')
                setShowAddressModal(false)
                await loadConsultation()
            } else {
                toast.error(
                    response.error || 'Erro ao alterar o local de atendimento',
                )
            }
        } catch {
            toast.error('Erro ao alterar o local de atendimento')
        } finally {
            setSavingAddress(false)
        }
    }

    /**
     * Troca manual de status. Cancelado fica fora das opções: cancelar mexe em
     * cobrança, estorno e agenda, e tem ação própria (o backend recusa por aqui).
     */
    const statusOptions = (
        Object.entries(CONSULTATION_STATUS_LABELS) as [string, string][]
    )
        .filter(([value]) => value !== 'CANCELLED')
        .map(([value, label]) => ({ value, label }))

    const handleChangeStatus = async (status: string) => {
        setSavingStatus(true)
        try {
            const res = await changeConsultationStatus(consultation!.id, status as ConsultationStatus)
            if (res.success) {
                toast.success('Status alterado.')
                setShowStatusModal(false)
                await loadConsultation()
            } else {
                toast.error(res.error || 'Não foi possível alterar o status.')
            }
        } finally {
            setSavingStatus(false)
        }
    }

    /**
     * Ações secundárias do cabeçalho. "Iniciar" (entrar na sala) é a primária e fica
     * fora do menu.
     *
     * Montada antes dos early-returns, então `consultation` pode ser null — o menu só
     * é renderizado depois deles.
     */
    const consultationActions: ActionMenuItem[] = consultation
        ? [
            {
                key: 'print',
                label: 'Imprimir',
                icon: <MdPrint />,
                onClick: () => handlePrint('print'),
                loading: printing,
            },
            {
                key: 'download',
                label: 'Baixar PDF',
                icon: <MdDownload />,
                onClick: () => handlePrint('download'),
                loading: printing,
            },
            ...(!isIntermediation
                ? [{
                    key: 'export-pdf',
                    label: 'Exportar PDF',
                    icon: <MdPictureAsPdf />,
                    onClick: () => setShowSignModal(true),
                    title: 'Exportar PDF com assinatura digital',
                }]
                : []),
            // Consulta cancelada não é editável (o backend recusa): editar o valor geraria
            // cobrança complementar ou estorno para um atendimento que não vai acontecer.
            // Marcar/remarcar a data sem atravessar a tela de edição, que carrega o
            // atendimento inteiro para quem só precisa dizer "é quinta às 14h".
            ...(canUpdate && consultation.status !== 'CANCELLED'
                ? [{
                    key: 'schedule',
                    label: consultation.scheduledAt ? 'Remarcar' : 'Agendar',
                    icon: <MdEvent />,
                    onClick: () => setShowScheduleModal(true),
                }]
                : []),
            // O local é confirmado uma vez, no agendamento, e depois disso não havia
            // como mexer: a tela de edição não mostra o campo.
            ...(canUpdate && consultation.status !== 'CANCELLED'
                ? [{
                    key: 'change-service-address',
                    label: 'Alterar endereço de atendimento',
                    icon: <MdLocationOn />,
                    onClick: () => setShowAddressModal(true),
                }]
                : []),
            // Régua do operador: a importação trouxe atendimento marcado como
            // concluído sem ter acontecido, e não havia como corrigir.
            ...(canUpdate && consultation.status !== 'CANCELLED'
                ? [{
                    key: 'change-status',
                    label: 'Alterar status',
                    icon: <MdFlag />,
                    onClick: () => setShowStatusModal(true),
                }]
                : []),
            ...(canUpdate && consultation.status !== 'CANCELLED'
                ? [{
                    key: 'edit',
                    label: 'Editar',
                    icon: <MdEdit />,
                    onClick: () =>
                        navigate(
                            `/atendimento/scheduling/${consultation.id}?type=CONSULTATION`,
                        ),
                }]
                : []),
            // Transferir vendedor: só o vendedor atual (createdBy) ou um administrador
            // (userType COMPANY). O backend reforça; aqui a ação nem aparece para os demais.
            ...((user?.userType === UserType.COMPANY ||
                user?.id === consultation.createdBy?.id) &&
                consultation.status !== 'CANCELLED'
                ? [{
                    key: 'transfer-seller',
                    label: 'Transferir vendedor',
                    icon: <MdSwapHoriz />,
                    onClick: () => setShowTransferModal(true),
                }]
                : []),
            // Gerar cobrança: quando a consulta não tem nenhuma cobrança VIVA nem dinheiro
            // retido — inclui a que teve a cobrança cancelada, estornada ou baixada como
            // perda, que antes ficava sem como cobrar. Consulta não tem modo orçamento.
            //
            // Diferente de exame e procedimento, a Consultation não persiste `finalPrice`
            // (o valor vive no Billing), então a tela não sabe calcular saldo parcial. Por
            // isso aqui a condição é só `shouldBecomeAnchor`: o backend, que recompõe o
            // preço pela cascata do catálogo, pode ainda aceitar um COMPLEMENTO que a tela
            // não oferece — nunca o contrário.
            ...(canUpdate && billingState.shouldBecomeAnchor && consultation.status !== 'CANCELLED'
                ? [{
                    key: 'generate-billing',
                    label: 'Gerar cobrança',
                    icon: <MdReceiptLong />,
                    onClick: handleGenerateBilling,
                    loading: generatingBilling,
                }]
                : []),
            // Renovar guia: redefine o vencimento da guia para hoje + dias configurados
            // em "Validade das Guias". Consulta não tem modo orçamento.
            ...(canUpdate && consultation.status !== 'CANCELLED'
                ? [{
                    key: 'renew-guide',
                    label: 'Renovar guia',
                    icon: <MdEventRepeat />,
                    onClick: () => setShowRenewGuideModal(true),
                    loading: renewingGuide,
                }]
                : []),
            // Cancelar preserva o registro; excluir apaga. São ações distintas.
            // Concluída TAMBÉM pode ser cancelada: o erro de lançamento costuma
            // aparecer depois de concluir, e o cancelamento é o caminho que já
            // aciona o estorno da cobrança.
            ...(canUpdate && consultation.status !== 'CANCELLED'
                ? [{
                    key: 'cancel',
                    label: `Cancelar ${consultationLabel}`,
                    icon: <MdCancel />,
                    onClick: () => setShowCancelModal(true),
                    danger: true,
                    loading: cancelling,
                }]
                : []),
            ...(canDelete
                ? [{
                    key: 'delete',
                    label: 'Excluir',
                    icon: <MdDelete />,
                    onClick: handleDelete,
                    danger: true,
                    loading: deleting,
                }]
                : []),
        ]
        : []

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando detalhes da consulta...
                </p>
            </div>
        )
    }

    if (!consultation) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                    <MdMedicalServices className="text-4xl text-base-content/30" />
                </div>
                <div className="text-center">
                    <h3 className="text-lg font-semibold text-base-content mb-1">
                        Consulta não encontrada
                    </h3>
                    <p className="text-sm text-base-content/60">
                        A consulta que você está procurando não existe
                    </p>
                </div>
                <button
                    onClick={() => goBack()}
                    className="btn btn-secondary"
                >
                    Voltar para Consultas
                </button>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => goBack()}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 hidden md:flex lg:flex rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 items-center justify-center shadow-lg">
                            <MdMedicalServices className="text-2xl text-white" />
                        </div>
                        <div>
                            <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                                {consultation.title || `Consulta #${consultation.id}`}
                            </h1>
                            <p className="text-sm text-base-content/60">
                                Detalhes da consulta
                            </p>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {/* Iniciar o atendimento é o motivo da visita: fica em evidência,
                        fora do menu. */}
                    {!isIntermediation &&
                        consultation.status !== 'COMPLETED' &&
                        consultation.status !== 'CANCELLED' && (
                        <button
                            onClick={() =>
                                navigate(`/bookings/consultations/${consultation.id}/room`)
                            }
                            className="btn btn-success btn-sm gap-2"
                        >
                            <MdPlayArrow className="text-lg" />
                            <span className="hidden sm:inline">Iniciar</span>
                        </button>
                    )}
                    {/* Pedido de exames: o médico emite a ordem durante a consulta,
                        sem preço e sem sair da tela. */}
                    <ExamRequestButton
                        clientId={consultation.client?.id}
                        clientName={consultation.client?.name}
                    />
                    {/* Prontuário do paciente sem sair do atendimento (só no segmento saúde). */}
                    <ProntuarioMenu
                        clientId={consultation.client?.id}
                        clientName={consultation.client?.name}
                    />
                    <ActionsMenu items={consultationActions} />
                </div>
            </div>

            {consultation.status === 'CANCELLED' && (
                <CancellationNotice
                    entityLabel="consulta"
                    cancelledAt={consultation.cancelledAt}
                    cancellationReason={consultation.cancellationReason}
                />
            )}

            {/* Timeline */}
            {!isIntermediation && <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <ConsultationTimeline
                    status={consultation.status}
                    transcriptionStatus={consultation.transcriptionStatus}
                    scheduledAt={consultation.scheduledAt}
                    startedAt={consultation.startedAt}
                    completedAt={consultation.completedAt}
                    transcribedAt={consultation.transcribedAt}
                    aiProcessedAt={consultation.aiProcessedAt}
                />
            </div>}

            {/* Info Cards */}
            <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <span
                                className={`badge ${getConsultationStatusColor(
                                    consultation.status,
                                    consultation.scheduledAt
                                )}`}
                            >
                                {getConsultationStatusLabel(
                                    consultation.status,
                                    consultation.scheduledAt
                                )}
                            </span>
                            {consultation.duration && (
                                <span className="text-sm text-base-content/60">
                                    Duração: {Math.floor(consultation.duration / 60)}min
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-base-content/70">
                            <MdCalendarToday />
                            <span>
                                {consultation.scheduledAt
                                    ? format(
                                        new Date(consultation.scheduledAt),
                                        "dd 'de' MMMM 'de' yyyy 'às' HH:mm",
                                        { locale: ptBR }
                                    )
                                    : format(
                                        new Date(consultation.createdAt),
                                        "dd 'de' MMMM 'de' yyyy 'às' HH:mm",
                                        { locale: ptBR }
                                    )}
                            </span>
                        </div>
                        <GuideValidityLine
                            issuedAt={consultation.createdAt}
                            days={guideValidityDays}
                            guideValidUntil={consultation.guideValidUntil}
                        />
                    </div>
                </div>

                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {consultation.client && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <MdPerson className="text-xl text-secondary" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Paciente
                                    </p>
                                    {/* O nome leva ao cadastro do paciente: quem abre o
                                        atendimento quase sempre precisa do histórico, do
                                        contato ou do convênio dele, e antes tinha de sair e
                                        buscar na listagem de clientes. */}
                                    <button
                                        onClick={() =>
                                            navigate(
                                                `/person/clients/${consultation.client!.id}`,
                                            )
                                        }
                                        className="font-semibold link link-hover text-left inline-flex items-center gap-1"
                                        title="Ver cadastro do paciente"
                                    >
                                        {consultation.client.name}
                                        <MdOpenInNew className="text-sm text-base-content/40" />
                                    </button>
                                    {consultation.client.email && (
                                        <p className="text-sm text-base-content/60">
                                            {consultation.client.email}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {consultation.professional && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <MdMedicalServices className="text-xl text-secondary" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Profissional
                                    </p>
                                    <p className="font-semibold">
                                        {consultation.professional.name}
                                    </p>
                                    {consultation.professional.registrationNumber && (
                                        <p className="text-sm text-base-content/60">
                                            {consultation.professional.councilName}{' '}
                                            {consultation.professional.registrationNumber}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Local de atendimento: fica ao lado do paciente e do
                            profissional porque é o que a recepção precisa dizer ao
                            telefone. Antes só existia no banco. */}
                        <ServiceAddressCard
                            address={consultation}
                            onChange={
                                canUpdate && consultation.status !== 'CANCELLED'
                                    ? () => setShowAddressModal(true)
                                    : undefined
                            }
                        />

                        {consultation.createdBy && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-info/10 rounded-lg">
                                    <MdManageAccounts className="text-xl text-info" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Agendado por
                                    </p>
                                    <p className="font-semibold">
                                        {consultation.createdBy.name}
                                    </p>
                                    {consultation.createdBy.email && (
                                        <p className="text-sm text-base-content/60">
                                            {consultation.createdBy.email}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {consultation.partner && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-accent/10 rounded-lg">
                                    <MdBusiness className="text-xl text-accent" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Parceiro
                                    </p>
                                    <p className="font-semibold">
                                        {consultation.partner.name}
                                    </p>
                                    {consultation.partner.tradingName && (
                                        <p className="text-sm text-base-content/60">
                                            {consultation.partner.tradingName}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                        {consultation.hospital && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-error/10 rounded-lg">
                                    <MdLocalHospital className="text-xl text-error" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Hospital
                                    </p>
                                    <p className="font-semibold">
                                        {consultation.hospital.tradingName ||
                                            consultation.hospital.name}
                                    </p>
                                    {consultation.hospital.tradingName &&
                                        consultation.hospital.name !==
                                            consultation.hospital.tradingName && (
                                            <p className="text-sm text-base-content/60">
                                                {consultation.hospital.name}
                                            </p>
                                        )}
                                    {consultation.hospital.cnpj && (
                                        <p className="text-xs text-base-content/50 mt-1">
                                            CNPJ: {consultation.hospital.cnpj}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                        {consultation.healthPlan && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-info/10 rounded-lg">
                                    <MdBusiness className="text-xl text-info" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Convênio
                                    </p>
                                    <p className="font-semibold">
                                        {consultation.healthPlan.name}
                                    </p>
                                    {consultation.healthPlan.ansCode && (
                                        <p className="text-xs text-base-content/50">
                                            ANS: {consultation.healthPlan.ansCode}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                        {(() => {
                            const roomData = (consultation as any).roomRef || (consultation as any).agendaEvents?.[0]?.room
                            if (!roomData) return null
                            return (
                                <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                    <div className="p-2 bg-secondary/10 rounded-lg">
                                        <MdMeetingRoom className="text-xl text-secondary" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs text-base-content/60 mb-1">Sala / Consultório</p>
                                        <p className="font-semibold">{roomData.name}</p>
                                        {roomData.description && (
                                            <p className="text-xs text-base-content/60">{roomData.description}</p>
                                        )}
                                    </div>
                                </div>
                            )
                        })()}
                    </div>

                    <PresenceTimeline kind="CONSULTATION" appointmentId={consultation.id} />

                    {/* Tipo de Consulta e Especialidade */}
                    {consultation.catalog && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <div className="p-3 bg-base-200/50 rounded-xl">
                                <p className="text-xs text-base-content/60">Tipo de Consulta</p>
                                <p className="font-semibold">{consultation.catalog.name}</p>
                            </div>
                            {(consultation.catalog.specialties?.length || consultation.catalog.specialty) && (
                                <div className="p-3 bg-base-200/50 rounded-xl">
                                    <p className="text-xs text-base-content/60">
                                        {(consultation.catalog.specialties?.length || 0) > 1 ? 'Especialidades' : 'Especialidade'}
                                    </p>
                                    <div className="flex flex-wrap gap-1 mt-0.5">
                                        {consultation.catalog.specialties?.length
                                            ? consultation.catalog.specialties.map((cs: any) => (
                                                <span key={cs.specialty.id} className="badge badge-secondary badge-sm">
                                                    {cs.specialty.name}
                                                </span>
                                            ))
                                            : consultation.catalog.specialty && (
                                                <span className="badge badge-secondary badge-sm">
                                                    {consultation.catalog.specialty.name}
                                                </span>
                                            )
                                        }
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Variações escolhidas, com quantidade.
                        Elas SUBSTITUEM o preço base da consulta (regra do
                        `consultation.service`), e cada unidade conta: sem esta
                        tabela o operador via um total que não conseguia explicar. */}
                    {(consultation as any).variations &&
                        (consultation as any).variations.length > 0 && (
                            <div className="mb-6">
                                <h3 className="mb-2 text-sm font-semibold text-base-content">
                                    Variações
                                </h3>
                                <div className="overflow-x-auto rounded-xl border border-base-300">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr className="text-xs text-base-content/50">
                                                <th>Variação</th>
                                                <th className="text-right">
                                                    Valor Unit.
                                                </th>
                                                <th className="text-center">
                                                    Qtd.
                                                </th>
                                                <th className="text-right">
                                                    Subtotal
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(consultation as any).variations.map(
                                                (v: any) => {
                                                    const unit = v.unitPrice ?? 0
                                                    const qtd = v.quantity ?? 1
                                                    return (
                                                        <tr key={v.id}>
                                                            <td className="font-medium">
                                                                {v.variationName ||
                                                                    `Variação #${v.variationId}`}
                                                            </td>
                                                            <td className="text-right">
                                                                R${' '}
                                                                {(
                                                                    unit / 100
                                                                ).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </td>
                                                            <td className="text-center">
                                                                {qtd}
                                                            </td>
                                                            <td className="text-right font-medium text-secondary">
                                                                R${' '}
                                                                {(
                                                                    (unit * qtd) /
                                                                    100
                                                                ).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </td>
                                                        </tr>
                                                    )
                                                }
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                    {/* Informações Financeiras */}
                    {(() => {
                        const catalog = consultation.catalog
                        const catalogPrice = catalog?.sellingPrice || catalog?.price || 0
                        if (catalogPrice <= 0) return null
                        const disc = consultation.discount || 0
                        // O cupom abate depois do desconto manual (valor congelado no atendimento).
                        const couponAmount = (consultation as any).couponCode ? ((consultation as any).couponDiscount ?? 0) : 0
                        const afterDiscount = disc > 0 ? Math.round(catalogPrice - (catalogPrice * disc / 100)) : catalogPrice
                        const finalVal = Math.max(0, afterDiscount - couponAmount)
                        return (
                            <div className="mb-6">

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="p-3 bg-base-200/50 rounded-xl">
                                        <p className="text-xs text-base-content/60">Valor </p>
                                        <p className="font-semibold text-secondary">R$ {(catalogPrice / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                    </div>
                                    {disc > 0 && (
                                        <div className="p-3 bg-base-200/50 rounded-xl">
                                            <p className="text-xs text-base-content/60">Desconto</p>
                                            <p className="font-semibold text-accent">{disc}%</p>
                                        </div>
                                    )}
                                    <AppointmentCoupon
                                        code={(consultation as any).couponCode}
                                        amount={couponAmount}
                                    />
                                    <div className="p-3 bg-secondary/10 rounded-xl">
                                        <p className="text-xs text-base-content/60">Valor Final</p>
                                        <p className="font-bold text-secondary">R$ {(finalVal / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                    </div>
                                </div>
                            </div>
                        )
                    })()}

                    {/* Pix único que pagou várias consultas zeradas por cupom (empresa que
                        sorteou entre os funcionários): o cupom continua registrado, e a parte
                        do Pix desta consulta mostra que um terceiro pagou. Fica FORA do bloco
                        acima de propósito: ele só aparece quando o catálogo tem preço próprio,
                        e a consulta precificada pelo parceiro (o caso do sorteio) não tem. Vem
                        do backend com a fatia ATUAL e some com a cobrança cancelada ou estornada. */}
                    {(() => {
                        const thirdPartyShare = (consultation as any).thirdPartyCoverage as
                            | { payer: string | null; amount: number }
                            | null
                            | undefined
                        if (!thirdPartyShare || consultation.status === 'CANCELLED') return null
                        const couponCode = (consultation as any).couponCode as string | null
                        return (
                            <div className="mb-6 p-4 rounded-xl bg-info/10 border border-info/20 flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-xs text-base-content/60">Pago por terceiro</p>
                                    <p className="font-bold text-info">
                                        R$ {(thirdPartyShare.amount / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </p>
                                    {thirdPartyShare.payer && (
                                        <p className="text-sm text-base-content/70 mt-0.5 break-words">{thirdPartyShare.payer}</p>
                                    )}
                                </div>
                                {couponCode && (
                                    <p className="text-xs text-base-content/60">Cupom {couponCode} registrado</p>
                                )}
                            </div>
                        )
                    })()}

                    {/* Triage Info */}
                    {!isIntermediation && consultation.triage && (
                        <div className="mb-6 p-4 bg-warning/10 rounded-xl border border-warning/20">
                            <h3 className="font-semibold text-base-content mb-2">
                                Triagem Vinculada
                            </h3>
                            <p className="text-sm">
                                <strong>Urgência:</strong> {consultation.triage.urgency}
                            </p>
                            {consultation.triage.chiefComplaint && (
                                <p className="text-sm mt-1">
                                    <strong>Queixa:</strong>{' '}
                                    {consultation.triage.chiefComplaint}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Notes */}
                    {consultation.notes && (
                        <div className="mb-6">
                            <h3 className="text-lg font-bold text-base-content mb-3">
                                Anotações
                            </h3>
                            <div className="p-4 bg-base-200/50 rounded-xl">
                                <p className="text-base-content whitespace-pre-wrap">
                                    {consultation.notes}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Diagnosis */}
                    {!isIntermediation && consultation.diagnosis && (
                        <div className="mb-6">
                            <h3 className="text-lg font-bold text-base-content mb-3">
                                Diagnóstico
                            </h3>
                            <div className="p-4 bg-base-200/50 rounded-xl">
                                <p className="text-base-content whitespace-pre-wrap">
                                    {consultation.diagnosis}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Treatment Plan */}
                    {!isIntermediation && consultation.treatmentPlan && (
                        <div className="mb-6">
                            <h3 className="text-lg font-bold text-base-content mb-3">
                                Plano de Tratamento
                            </h3>
                            <div className="p-4 bg-base-200/50 rounded-xl">
                                <p className="text-base-content whitespace-pre-wrap">
                                    {consultation.treatmentPlan}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Billing */}
            {consultation.billing && (
                <BillingInfoSection
                    // Cobrança pode ser agrupada: o card usa isto para mostrar quanto
                    // do total é deste atendimento.
                    appointmentSource={{ sourceType: 'CONSULTATION', sourceId: consultation.id }}
                    billing={consultation.billing as BillingInfoData}
                    onMarkAsPaid={async () => {
                        const response = await markConsultationBillingAsPaid(consultation.id)
                        if (response.success) {
                            const result = response.result as any
                            if (result?.pending) {
                                toast.info(
                                    result.message ||
                                        'A solicitação de aprovação para marcar como pago foi enviada'
                                )
                            } else {
                                toast.success('Cobrança marcada como paga!')
                            }
                            loadConsultation()
                        } else {
                            toast.error(response.error || 'Erro ao marcar como paga')
                        }
                    }}
                    onCancel={async () => {
                        const response = await cancelConsultationBilling(consultation.id)
                        if (response.success) {
                            const result = response.result as any
                            if (result?.pending) {
                                toast.info(
                                    result.message ||
                                        'A solicitação de aprovação para cancelar a cobrança foi enviada'
                                )
                            } else {
                                toast.success('Cobrança cancelada!')
                            }
                            loadConsultation()
                        } else {
                            toast.error(response.error || 'Erro ao cancelar cobrança')
                        }
                    }}
                    canUpdate={canUpdate}
                    onBillingChanged={loadConsultation}
                />
            )}

            {/* Complementos/parcelas: cada cobrança filha com o MESMO bloco completo
                (comprovante, marcar como pago etc.). */}
            <BillingChildrenSections
                adjustments={(consultation.billing as any)?.adjustments}
                onReload={loadConsultation}
                canUpdate={canUpdate}
            />

            {/* Deal / Pipeline */}
            {consultation.deal && (
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4 flex items-center gap-2">
                            <MdTrendingUp className="text-secondary" />
                            Funil de Vendas
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <span className="text-sm text-base-content/60">Pipeline</span>
                                <p className="font-medium">{consultation.deal.pipeline.name}</p>
                            </div>
                            <div>
                                <span className="text-sm text-base-content/60">Etapa</span>
                                <div className="flex items-center gap-2">
                                    <span
                                        className="w-3 h-3 rounded-full inline-block"
                                        style={{ backgroundColor: consultation.deal.stage.color || '#6b7280' }}
                                    />
                                    <span className="font-medium">{consultation.deal.stage.name}</span>
                                </div>
                            </div>
                            <div>
                                <span className="text-sm text-base-content/60">Oportunidade</span>
                                <p className="font-medium">{consultation.deal.title}</p>
                            </div>
                            <div>
                                <span className="text-sm text-base-content/60">Status</span>
                                <div>
                                    <span className={`badge badge-sm ${
                                        consultation.deal.status === 'WON' ? 'badge-success' :
                                        consultation.deal.status === 'LOST' ? 'badge-error' :
                                        consultation.deal.status === 'ABANDONED' ? 'badge-warning' :
                                        'badge-info'
                                    }`}>
                                        {consultation.deal.status === 'OPEN' ? 'Aberta' :
                                         consultation.deal.status === 'WON' ? 'Ganha' :
                                         consultation.deal.status === 'LOST' ? 'Perdida' : 'Abandonada'}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <span className="text-sm text-base-content/60">Lead</span>
                                <p className="font-medium">{consultation.deal.lead.name}</p>
                                {consultation.deal.lead.phoneNumber && (
                                    <p className="text-sm text-base-content/60">{consultation.deal.lead.phoneNumber}</p>
                                )}
                            </div>
                            {consultation.deal.totalValue != null && consultation.deal.totalValue > 0 && (
                                <div>
                                    <span className="text-sm text-base-content/60">Valor da Oportunidade</span>
                                    <p className="font-medium text-secondary">
                                        {(consultation.deal.totalValue / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="mt-4">
                            <a
                                href={`/sales/opportunities/${consultation.deal.id}`}
                                className="btn btn-sm btn-outline btn-secondary"
                            >
                                Ver Oportunidade
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {/* Audio Player */}
            {!isIntermediation && consultation.audioUrl && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-secondary/10 rounded-lg">
                            <MdHeadset className="text-2xl text-secondary" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-base-content">
                                Áudio da Consulta
                            </h2>
                            <p className="text-sm text-base-content/60">
                                Gravação da consulta realizada
                            </p>
                        </div>
                    </div>

                    <div className="bg-base-200/50 rounded-xl p-4">
                        <audio
                            controls
                            className="w-full"
                            preload="metadata"
                        >
                            <source src={consultation.audioUrl} type={consultation.audioFormat || 'audio/mpeg'} />
                            Seu navegador não suporta o elemento de áudio.
                        </audio>

                        {/* Audio Info */}
                        <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-base-content/70">
                            {consultation.audioDuration && (
                                <div className="flex items-center gap-2">
                                    <MdAccessTime className="text-base" />
                                    <span>
                                        Duração: {Math.floor(consultation.audioDuration / 60)}:
                                        {String(consultation.audioDuration % 60).padStart(2, '0')}
                                    </span>
                                </div>
                            )}
                            {consultation.audioFormat && (
                                <div className="flex items-center gap-2">
                                    <span className="badge badge-sm">
                                        {consultation.audioFormat}
                                    </span>
                                </div>
                            )}
                            {consultation.audioSize && (
                                <div className="flex items-center gap-2">
                                    <MdStorage className="text-base" />
                                    <span>
                                        {(consultation.audioSize / (1024 * 1024)).toFixed(2)} MB
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Audio & Transcription Section */}
            {!isIntermediation && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Transcription */}
                <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-bold text-base-content">
                            Transcrição
                        </h2>
                        {consultation.audioUrl &&
                            consultation.transcriptionStatus === 'PENDING' && (
                                <button
                                    onClick={handleTranscribe}
                                    disabled={transcribing}
                                    className="btn btn-sm btn-secondary gap-2"
                                >
                                    {transcribing ? (
                                        <span className="loading loading-spinner loading-sm"></span>
                                    ) : (
                                        <MdMic />
                                    )}
                                    Transcrever
                                </button>
                            )}
                    </div>
                    <TranscriptionViewer
                        transcription={consultation.transcription || ''}
                        status={consultation.transcriptionStatus}
                        transcribedAt={consultation.transcribedAt}
                        maxHeight="300px"
                    />
                </div>

                {/* AI Summary */}
                <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-bold text-base-content">
                            Análise IA
                        </h2>
                        {consultation.transcriptionStatus === 'COMPLETED' &&
                            !consultation.aiSummary && (
                                <button
                                    onClick={handleAnalyze}
                                    disabled={analyzing}
                                    className="btn btn-sm btn-secondary gap-2"
                                >
                                    {analyzing ? (
                                        <span className="loading loading-spinner loading-sm"></span>
                                    ) : (
                                        <MdAutoAwesome />
                                    )}
                                    Gerar Resumo
                                </button>
                            )}
                    </div>
                    <AiSummaryCard
                        summary={consultation.aiSummary}
                        diagnosis={consultation.aiDiagnosis}
                        recommendations={consultation.aiRecommendations}
                        followUp={consultation.aiFollowUp}
                        extractedData={consultation.aiExtractedData}
                        clientId={consultation.clientId}
                        consultationId={consultation.id}
                        confidenceScore={consultation.aiConfidenceScore}
                        model={consultation.aiModel}
                        processedAt={consultation.aiProcessedAt}
                        onRegenerate={
                            consultation.aiSummary ? handleRegenerateSummary : undefined
                        }
                        isRegenerating={analyzing}
                    />
                </div>
            </div>}

            <CancelAppointmentModal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={confirmCancel}
                loading={cancelling}
                entityLabel="consulta"
                billing={consultation.billing ?? null}
            />

            <ConfirmModal
                isOpen={showRenewGuideModal}
                onClose={() => setShowRenewGuideModal(false)}
                onConfirm={handleRenewGuide}
                title="Renovar guia"
                message={`Renovar a validade da guia para ${format(
                    computeRenewedGuideValidUntil(guideValidityDays),
                    "dd 'de' MMMM 'de' yyyy",
                    { locale: ptBR },
                )}?`}
                confirmButtonText="Renovar"
                cancelButtonText="Cancelar"
                loading={renewingGuide}
            />

            {showStatusModal && (
                <ChangeStatusModal
                    entityLabel="consulta"
                    currentStatus={consultation.status}
                    options={statusOptions}
                    saving={savingStatus}
                    onClose={() => setShowStatusModal(false)}
                    onConfirm={handleChangeStatus}
                />
            )}

            <ChangeServiceAddressModal
                isOpen={showAddressModal}
                onClose={() => setShowAddressModal(false)}
                onConfirm={handleChangeServiceAddress}
                loading={savingAddress}
                current={consultation}
                partnerId={consultation?.partnerId ?? null}
                professionalId={consultation?.professionalId ?? null}
                entityLabel="consulta"
            />

            <ScheduleAppointmentModal
                isOpen={showScheduleModal}
                onClose={() => setShowScheduleModal(false)}
                serviceType="CONSULTATION"
                recordId={consultation.id}
                currentScheduledAt={consultation.scheduledAt}
                currentArrivalOrder={(consultation as any).arrivalOrder}
                onScheduled={loadConsultation}
            />

            <TransferSellerModal
                isOpen={showTransferModal}
                onClose={() => setShowTransferModal(false)}
                onConfirm={confirmTransferSeller}
                loading={transferring}
                currentSellerName={consultation.createdBy?.name}
                companyId={consultation.companyId}
            />

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                title="Excluir consulta"
                message="Tem certeza que deseja excluir esta consulta?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />

            {!isIntermediation && consultation && (
                <SignDocumentModal
                    isOpen={showSignModal}
                    onClose={() => setShowSignModal(false)}
                    documentType="CONSULTATION"
                    documentId={consultation.id}
                    documentTitle={`Consulta - ${consultation.client?.name || ''}`}
                    onSigned={() => {
                        setShowSignModal(false)
                    }}
                />
            )}
        </div>
    )
}
